import { useMutation } from '@tanstack/react-query';
import {
  type AdminUserSearchResultDto,
  UsersApi,
} from '@up2stay/up2promo-api-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Input,
  Label,
} from '@up2stay/up2promo-ui';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { SupportImpersonateCombobox } from '@/core/components/support/support-impersonate-combobox';
import { useErrorMessage } from '@/core/hooks/use-error-message';
import { useIsPlatformAdmin } from '@/core/hooks/use-is-platform-admin';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { getApiValidationErrorCode } from '@/core/utils/api-error.utils';

function SupportPasswordBreadcrumb({
  domainLabel,
  pageLabel,
}: {
  domainLabel: string;
  pageLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {pageLabel}
    </>
  );
}

export function SupportSetPasswordPage() {
  const dict = useDictionary();
  const locale = useLocale();
  const pageDict = dict.support.password;
  const getErrorMessage = useErrorMessage();
  const isPlatformAdmin = useIsPlatformAdmin();
  const [selectedUser, setSelectedUser] =
    useState<AdminUserSearchResultDto | null>(null);
  const [password, setPassword] = useState('');
  const [setDialogOpen, setSetDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const setPasswordMutation = useMutation({
    mutationFn: async ({
      userId,
      nextPassword,
    }: {
      userId: string;
      nextPassword: string;
    }) => {
      await UsersApi.setPassword({
        path: { id: userId },
        body: { password: nextPassword },
      });
    },
    onSuccess: () => {
      toast.success(pageDict.setSuccess);
      setPassword('');
      setSetDialogOpen(false);
      if (selectedUser) {
        setSelectedUser({ ...selectedUser, hasPassword: true });
      }
    },
    onError: (err) => {
      const errorCode = getApiValidationErrorCode(err, 'password');
      toast.error(getErrorMessage(errorCode, { fallback: pageDict.setError }));
    },
  });

  const clearPasswordMutation = useMutation({
    mutationFn: async (userId: string) => {
      await UsersApi.clearPassword({
        path: { id: userId },
      });
    },
    onSuccess: () => {
      toast.success(pageDict.clearSuccess);
      setClearDialogOpen(false);
      if (selectedUser) {
        setSelectedUser({ ...selectedUser, hasPassword: false });
      }
    },
    onError: (err) => {
      const errorCode = getApiValidationErrorCode(err, 'id');
      toast.error(
        getErrorMessage(errorCode, { fallback: pageDict.clearError }),
      );
    },
  });

  if (!isPlatformAdmin) {
    return <Navigate to={shellPaths.moreHub} replace />;
  }

  const passwordTrimmed = password.trim();
  const passwordValid = passwordTrimmed.length >= 8;
  const isBusy =
    setPasswordMutation.isPending || clearPasswordMutation.isPending;

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="gap-6 pb-8">
          <PageHeader
            breadcrumb={
              <SupportPasswordBreadcrumb
                domainLabel={dict.nav.supportTools}
                pageLabel={dict.nav.supportPassword}
              />
            }
            title={pageDict.title}
            backHref={shellPaths.moreSupport}
          />
          <SettingsSection
            id="support-set-password-user"
            title={pageDict.userLabel}
            grouped
            footer={pageDict.description}
          >
            <SettingsGroupRows>
              <SettingsGroupRow>
                <SupportImpersonateCombobox
                  selectedUser={selectedUser}
                  onSelect={(user) => {
                    setSelectedUser(user);
                    setPassword('');
                  }}
                  disabled={isBusy}
                  className="w-full max-w-sm"
                  placeholder={pageDict.placeholder}
                  searchPlaceholder={pageDict.searchPlaceholder}
                  emptyMessage={pageDict.emptyMessage}
                  loadingMessage={pageDict.loadingMessage}
                  locale={locale}
                />
              </SettingsGroupRow>
              {selectedUser ? (
                <SettingsGroupRow separated>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant={
                        selectedUser.hasPassword ? 'secondary' : 'outline'
                      }
                    >
                      {selectedUser.hasPassword
                        ? pageDict.passwordSetBadge
                        : pageDict.passwordNotSetBadge}
                    </Badge>
                  </div>
                </SettingsGroupRow>
              ) : null}
              <SettingsGroupRow separated>
                <div className="flex flex-wrap gap-2">
                  <Button
                    disabled={!selectedUser || isBusy}
                    onClick={() => setSetDialogOpen(true)}
                  >
                    {pageDict.setButton}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={
                      !selectedUser || !selectedUser.hasPassword || isBusy
                    }
                    onClick={() => setClearDialogOpen(true)}
                  >
                    {pageDict.clearButton}
                  </Button>
                </div>
              </SettingsGroupRow>
            </SettingsGroupRows>
          </SettingsSection>
        </AppPageLayout>
      </AppPageContent>

      <AlertDialog open={setDialogOpen} onOpenChange={setSetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pageDict.setDialogTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {pageDict.setDialogDescription.replace(
                '{email}',
                selectedUser?.email ?? selectedUser?.displayName ?? '',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 py-2">
            <Label htmlFor="support-set-password-input">
              {pageDict.passwordLabel}
            </Label>
            <Input
              id="support-set-password-input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={pageDict.passwordPlaceholder}
            />
            {password.length > 0 && !passwordValid ? (
              <p className="text-sm text-destructive">
                {pageDict.passwordTooShort}
              </p>
            ) : null}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={setPasswordMutation.isPending}>
              {pageDict.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={!passwordValid || setPasswordMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (!selectedUser || !passwordValid) return;
                setPasswordMutation.mutate({
                  userId: selectedUser.id,
                  nextPassword: passwordTrimmed,
                });
              }}
            >
              {setPasswordMutation.isPending
                ? pageDict.saving
                : pageDict.setButton}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pageDict.clearDialogTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {pageDict.clearDialogDescription.replace(
                '{email}',
                selectedUser?.email ?? selectedUser?.displayName ?? '',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearPasswordMutation.isPending}>
              {pageDict.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={clearPasswordMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (!selectedUser) return;
                clearPasswordMutation.mutate(selectedUser.id);
              }}
            >
              {clearPasswordMutation.isPending
                ? pageDict.clearing
                : pageDict.clearButton}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppIonPage>
  );
}
